/**
 * PWA Update Handler (without virtual module imports)
 */
import { toast } from "sonner";
import i18next from "i18next";

const UPDATE_TOAST_ID = "pwa-update";
let updateToastShown = false;
let hasReloaded = false;

function showUpdateToast(waitingWorker: ServiceWorker) {
  if (updateToastShown) return;
  updateToastShown = true;

  const t = i18next.t.bind(i18next);
  toast.info(t("pwa.updateAvailable", "Neue Version verfügbar"), {
    id: UPDATE_TOAST_ID,
    duration: Infinity,
    action: {
      label: t("pwa.reload", "Neu laden"),
      onClick: () => waitingWorker.postMessage({ type: "SKIP_WAITING" }),
    },
  });
}

function attachUpdateListener(registration: ServiceWorkerRegistration) {
  registration.addEventListener("updatefound", () => {
    const newWorker = registration.installing;
    if (!newWorker) return;

    newWorker.addEventListener("statechange", () => {
      if (newWorker.state !== "installed") return;

      const t = i18next.t.bind(i18next);
      if (navigator.serviceWorker.controller) {
        showUpdateToast(newWorker);
      } else {
        toast.success(t("pwa.offlineReady", "App ist offline verfügbar"), {
          duration: 4000,
        });
      }
    });
  });
}

export function registerPwaUpdates() {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;

  // TES-2046: On the very FIRST visit there is no controller yet. The freshly
  // registered service worker precaches, activates with clientsClaim() and fires
  // "controllerchange" for its initial takeover a few seconds after page load.
  // Reloading then throws away live, unsaved UI state (e.g. a checklist mid-build
  // in the editor) — this made the Nightly QA Fullpass deterministically red
  // (P1 editor remount) and hits real first-time users the same way. Only reload
  // when an EXISTING controller is replaced (a real autoUpdate version switch).
  let hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) {
      hadController = true; // initial claim on first install — keep the page alive
      return;
    }
    if (hasReloaded) return;
    hasReloaded = true;
    window.location.reload();
  });

  window.addEventListener("load", async () => {
    try {
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      const registration =
        existingRegistration ?? (await navigator.serviceWorker.register("/sw.js"));

      if (!registration) return;

      if (registration.waiting && navigator.serviceWorker.controller) {
        showUpdateToast(registration.waiting);
      }

      attachUpdateListener(registration);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          registration.update().catch(() => undefined);
        }
      });

      await registration.update().catch(() => undefined);
    } catch (e) {
      console.debug("[PWA] Service worker registration skipped:", e);
    }
  });
}
